import { html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './maturity-and-duration-chart.constants.js';
import type { MaturityAndDurationChartContentObject } from './maturity-and-duration-chart.model.js';
import { Options } from 'highcharts';
import { BeaconChartBase } from '@trp-beacon-ui-kit/foundation/components';
import { formatToNDecimalPlaces } from '../../common/utils/formatNumber.js';
import { DISPLAY_TAB } from '../maturity-and-duration/maturity-and-duration.constants.js';

/**
 * @tag pop-maturity-and-duration-chart-component
 * @attribute { MaturityAndDurationChartContentObject } [data-component-object] MaturityAndDurationChart content object
 */
@customElement(Constants.TAG_NAME)
export class PopMaturityAndDurationChartComponent extends PopBaseComponent {
  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * MaturityAndDurationChart content object
   */
  componentObject?: MaturityAndDurationChartContentObject;

  firstUpdated(): void {
    const chart = this.shadowRoot?.querySelector('beacon-bar-chart');

    if (chart) {
      // This is necessary because the pointFormatter option for the tooltip is a function and doesn't
      // work correctly when only passed in the options attribute to the beacon-bar-chart element.
      chart.options = this.getOptions();
    }
  }

  private getSeriesData() {
    const maturityAndDurationData =
      this.componentObject?.itemList?.map((item) => ({
        name: item.name,
        y: item.percentOfFund,
        benchmark: item.percentOfIndicativeBenchmark,
      })) || [];
    return [
      {
        name: this.componentObject?.label,
        data: maturityAndDurationData,
      },
    ];
  }

  private getMaturityDurationNames() {
    return this.componentObject?.itemList?.map((item) => item.name) || [];
  }

  private getOptions(): Options {
    const showBenchmarkTooltip =
      this.componentObject?.showBenchmarkData === true;

    return {
      tooltip: {
        useHTML: true,
        headerFormat: BeaconChartBase.tooltipHeaderHTML('{point.key}'),
        pointFormatter: function () {
          return `
            ${BeaconChartBase.multiLevelTooltipPointHTML(Constants.PERCENT_OF_PORTFOLIO_LABEL, `${formatToNDecimalPlaces(this.y, 2)}%`, true)}
            ${
              showBenchmarkTooltip
                ? BeaconChartBase.multiLevelTooltipPointHTML(
                    Constants.BENCHMARK_LABEL,
                    `${formatToNDecimalPlaces(this.benchmark, 2)}%`,
                    true,
                  )
                : ''
            }
          `;
        },
      },
      exporting: {
        enabled: false,
      },
    };
  }

  render() {
    return this.renderComponent(html`
      <section class="maturity-and-duration-chart-content">
        <beacon-bar-chart
          variant="vertical"
          tooltip-variant="multi-level"
          y-axis-label="${Constants.PERCENT_OF_PORTFOLIO_LABEL}"
          x-axis-label="${this.componentObject?.label === DISPLAY_TAB.MATURITY
            ? Constants.MATURITY_LABEL
            : Constants.DURATION_LABEL}"
          series="${JSON.stringify(this.getSeriesData())}"
          categories="${this.getMaturityDurationNames()}"
          options="${JSON.stringify(this.getOptions())}"
          disable-legend="true"
        >
        </beacon-bar-chart>
      </section>
    `);
  }
}
